import { collection } from "firebase/firestore";
import { auth, db } from "@/services/firebase/firebase";

/**
 * Get the appointments collection for the current user
 */
export const appointmentsCol = (uid: string) =>
  collection(db, "appointments", uid, "items");

/**
 * Get the appointment requests collection for the current user
 */
export const requestsCol = (uid: string) =>
  collection(db, "appointmentRequests", uid, "items");

/**
 * Get the current authenticated user
 * @throws {Error} If user is not authenticated
 */
export const getCurrentUser = () => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");
  return user;
};

/**
 * Check if a time slot overlaps with existing appointments
 */
export const checkTimeSlotOverlap = (
  existingAppointments: any[],
  newDate: string,
  newTime: string,
  duration: number = 60
): boolean => {
  const newStart = new Date(`${newDate}T${newTime}`);
  const newEnd = new Date(newStart.getTime() + duration * 60000);

  return existingAppointments.some((apt) => {
    const existingStart = new Date(`${apt.date}T${apt.time}`);
    const existingEnd = new Date(existingStart.getTime() + (apt.duration || 60) * 60000);

    // Check if times overlap
    return (
      (newStart < existingEnd && newEnd > existingStart) ||
      (existingStart < newEnd && existingEnd > newStart)
    );
  });
};

/**
 * Generate a unique appointment ID
 */
export const generateAppointmentId = (): string => {
  return `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate appointment data
 */
export const validateAppointmentData = (data: any): string[] => {
  const errors: string[] = [];

  if (!data.clientName?.trim()) {
    errors.push("Client name is required");
  }

  if (!data.clientEmail?.trim()) {
    errors.push("Client email is required");
  }

  if (!data.date) {
    errors.push("Appointment date is required");
  }

  if (!data.time) {
    errors.push("Appointment time is required");
  }

  if (!data.specialty?.trim()) {
    errors.push("Legal specialty is required");
  }

  return errors;
};

/**
 * Format error messages for user display
 */
export const formatFirebaseError = (error: any): string => {
  if (error?.code) {
    switch (error.code) {
      case "permission-denied":
        return "You don't have permission to perform this action";
      case "not-found":
        return "The requested appointment was not found";
      case "already-exists":
        return "An appointment already exists for this time slot";
      case "failed-precondition":
        return "This operation cannot be completed at this time";
      default:
        return error.message || "An unexpected error occurred";
    }
  }
  return error?.message || "An unexpected error occurred";
};
